import { useState } from 'react';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { BaseModal } from '@/components/shared';
import { useToast } from '@/hooks/useToast';
import { montarCampos, montarCamposEtapa2, montarComSeguranca } from '../../../utils/respostasCandidato';
import { baixarPdfRespostasCandidato } from '../../../utils/respostasCandidatoPdf';
import type { Candidato } from '../../../models';

interface VisualizarCandidatoModalProps {
  candidato: Candidato | null;
  onClose: () => void;
}

/** Só leitura — respostas vêm exclusivamente do próprio candidato (ver
 * submitCandidatoExterno / submitCandidatoExternoEtapa2), nada aqui é editável. */
export default function VisualizarCandidatoModal({ candidato, onClose }: VisualizarCandidatoModalProps) {
  // Um campo gravado num formato inesperado (ex.: regime de trabalho como texto, de quando a
  // function ainda esperava texto) derrubava a tela inteira — tela branca. Agora só aquela
  // parte mostra um aviso e o resto aparece normalmente.
  const campos = candidato ? montarComSeguranca(() => montarCampos(candidato)) : [];
  const camposEtapa2 = candidato ? montarComSeguranca(() => montarCamposEtapa2(candidato)) : [];
  const toast = useToast();
  const [gerandoPdf, setGerandoPdf] = useState(false);

  // Pedido do RH: baixar as perguntas e respostas (Etapas 1 e 2) — mesmo conteúdo desta tela.
  const baixarPdf = async () => {
    if (!candidato) return;
    setGerandoPdf(true);
    try {
      await baixarPdfRespostasCandidato(candidato);
    } catch (error: any) {
      toast.error('Não foi possível gerar o PDF.', error?.message || 'Erro desconhecido.');
    } finally {
      setGerandoPdf(false);
    }
  };

  return (
    <BaseModal
      isOpen={candidato != null}
      onClose={onClose}
      title={candidato?.nome ?? ''}
      maxWidth="max-w-2xl"
      headerActions={
        campos.length > 0 || camposEtapa2.length > 0 ? (
          <button
            type="button"
            onClick={baixarPdf}
            disabled={gerandoPdf}
            className="text-gray-400 hover:text-indigo-600 disabled:opacity-40"
            title="Baixar PDF (perguntas e respostas)"
          >
            <span className="sr-only">Baixar PDF</span>
            <ArrowDownTrayIcon className={gerandoPdf ? 'h-6 w-6 animate-pulse' : 'h-6 w-6'} />
          </button>
        ) : null
      }
    >
      <p className="text-sm text-gray-500">{candidato?.email}</p>

      {campos.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500">Este candidato ainda não preencheu o formulário.</p>
      ) : (
        <dl className="mt-4 space-y-4">
          {campos.map((campo) => (
            <div key={campo.label}>
              <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">{campo.label}</dt>
              <dd className="mt-0.5 whitespace-pre-wrap text-sm text-gray-800">{campo.valor}</dd>
            </div>
          ))}
        </dl>
      )}

      {camposEtapa2.length > 0 && (
        <>
          <h3 className="mt-6 border-t border-gray-100 pt-4 text-sm font-semibold text-gray-700">
            Etapa 2 — Admissão
          </h3>
          <dl className="mt-4 space-y-4">
            {camposEtapa2.map((campo) => (
              <div key={campo.label}>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-400">{campo.label}</dt>
                <dd className="mt-0.5 whitespace-pre-wrap text-sm text-gray-800">{campo.valor}</dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </BaseModal>
  );
}
